import { html, internalProperty, LitElement, property, query } from 'lit-element'
import { ifDefined } from 'lit-html/directives/if-defined'
import { register } from '@polleverywhere/hmr-custom-element'

import '@polleverywhere/pe-text-field'
import '@polleverywhere/pe-graphic'

import './password-field.scss'

/**
 * Wrapper around pe-text-field to ensure our password requirements are validated.
 *
 * Takes most of the same options as pe-text-field
 *
 * @element pe-password-field
 *
 * @prop {boolean} current - Set this to use our legacy password standards,
 *  when requesting a users current password
 * @prop {boolean} disabled - Disables the text field from input.
 * @prop {string} error - Contains the error validation message whenever the
 *  input field is in an invalid state. Consumers can set this attribute to
 *  manually trigger their own validation error.
 * @prop {string} name - Give the `<input>` element a name. This name will also
 *  show up in validation messages as the subject.
 * @prop {string} placeholder - Help text to display in the input field.
 * @prop {boolean} required - Make the input field required.
 * @prop {string} value - Default value to initialize input field to. Consumers
 *  should use the value property to update the input field afterwards.
 * @prop {boolean} useDefaultPlaceholder - Enable native placeholder behavior
 *  instead of our custom animating one.
 *
 * @prop {boolean} active - For internal use, it evaluates whether the input
 *  field is in an "active" state, containing input or having document focus.
 * @prop {HTMLElement} field - Returns the input or textarea element.
 * @prop {boolean} focused - For internal use, returns whether field has focus.
 * @prop {boolean} valid - Returns whether the text field is in a valid state.
 * @prop {string} value - Get/set the input element's value.
 */
export default class PasswordField extends LitElement {
  static get tagName () { return 'pe-password-field' }

  @property({ type: Boolean })
  current

  @internalProperty({ type: Boolean })
  revealPassword

  // Passthrough props to text-field
  @property({ type: Boolean })
  disabled

  @property({ type: String })
  error

  @property({ type: String })
  name

  @property({ type: String })
  placeholder

  @property({ type: Boolean })
  required

  @property({ type: String })
  value

  @property({ type: Boolean, attribute: 'use-default-placeholder' })
  useDefaultPlaceholder

  @query('pe-text-field')
  textField

  get active () { return !!this.textField?.active }
  get field () { return this.textField?.field }
  get focused () { return !!this.textField?.focused }
  get valid () { return !!this.textField?.valid }

  get error () {
    const parent = this.textField
    return parent
      ? parent.error
      : this.getAttribute('error') || undefined
  }

  set error (message) {
    const parent = this.textField
    if (parent) parent.error = message
  }

  get value () {
    const field = this.field
    return field
      ? field.value
      : this.getAttribute('value') || undefined
  }

  set value (val) {
    const parent = this.textField
    if (parent) parent.value = val
  }

  createRenderRoot () { return this }

  connectedCallback () {
    super.connectedCallback()

    // Load zxcvbn in the background
    this.zxcvbn = false
    import(/* webpackChunkName: 'zxcvbn' */ 'zxcvbn').then((imported) => {
      this.zxcvbn = imported.default
    })
  }

  render () {
    return html`<pe-text-field
      autocapitalize=off
      autocomplete=${this.current ? 'current-password' : 'new-password'}
      autocorrect=off
      ?disabled=${this.disabled}
      error=${ifDefined(this.error)}
      maxLength=255
      minLength=${this.current ? 7 : 10}
      pattern='.{7,255}'
      name=${ifDefined(this.name)}
      placeholder=${ifDefined(this.placeholder)}
      ?required=${this.required}
      type=${this.revealPassword ? 'text' : 'password'}
      ?use-default-placeholder=${this.useDefaultPlaceholder}
      value=${ifDefined(this.value)}
    >
      <div slot='after'>
        <button
          class="pe-text-field__reveal"
          title=${this.revealPassword ? 'Hide password' : 'Show password'}
          type=button
          @click=${() => { this.revealPassword = !this.revealPassword }}
          >
          <pe-graphic
            name=${this.revealPassword ? 'hide-results' : 'show-chart'}
            type='legacy-glyph'
          ></pe-graphic>
        </button>
      </div>
    </pe-text-field>
    `
  }

  complexity () {
    const data = { complex: true, message: '' }

    // Return default (failing open) if zxcvbn has not loaded yet
    if (!this.zxcvbn) return data

    const result = this.zxcvbn(this.field.value || '2')
    const notes = result.feedback.warning || 'Try adding some more words.'

    data.complex = result.score >= 2
    data.message = `For your security please use a stronger password. ${notes}`

    return data
  }

  /**
   * Validate the input field and toggle the error state.
   * @return {boolean} True if input field is valid. False otherwise.
   */
  validate () {
    const parentValid = this.textField.validate()

    // No need to check complexity for already invalid or current passwords
    if (parentValid && !this.current) {
      const complexity = this.complexity()

      if (!complexity.complex) {
        this.error = complexity.message
      }
    }

    this.requestUpdate()

    return this.valid
  }
}

register(module, PasswordField.tagName, PasswordField)
